'use client';

import { useEffect, useState, useRef } from 'react';
import { useUnit } from 'effector-react';
import { getPortalPages } from '@nx-frontend/shared/api/webnotes/client';
import { useDebounce } from '@nx-frontend/shared/hooks';
import { $currentWorkspace } from '@nx-frontend/shared/store/current-workspace';
import { $menuState } from '@nx-frontend/shared/store/feature-menu-tree';
import { $note, $isNoteFetching } from '@nx-frontend/shared/store/note';
import { $notes } from '@nx-frontend/shared/store/notes';
import { MenuItem, MenuType } from '@nx-frontend/shared/types';
import { NotesStoreNote } from '@nx-frontend/shared/types';
import { MenusState } from '@nx-frontend/shared/types';

export const useGetNoteInMenu = () => {
  const [menuState, notes, currentWorkspace, isNoteFetching, currentNote] = useUnit([
    $menuState,
    $notes,
    $currentWorkspace,
    $isNoteFetching,
    $note,
  ]);
  const [noteInMenu, setNoteInMenu] = useState<MenuItem | null>(null);

  const workspaceId = currentWorkspace.globalId;
  const noteId = currentNote.globalId;
  const entities = notes.workspaces[workspaceId]?.entities || {};
  const note: NotesStoreNote | undefined = entities[noteId];

  const isFetchPortalPageExecutionCanceled = useRef<boolean>(false);
  // debouncing the function, because function in useEffect below is called too often
  // we don't want to send many the same requests to the server
  const fetchPortalPagesDebounced = useDebounce<{
    workspaceId: string;
    noteId: string;
    menuState: MenusState;
  }>(async ({ workspaceId, noteId, menuState }) => {
    if (!workspaceId) {
      // getPortalPages requires workspaceId
      return;
    }
    isFetchPortalPageExecutionCanceled.current = false;
    const portalPages = await getPortalPages({
      workspaceId,
      noteId,
    });
    if (isFetchPortalPageExecutionCanceled.current) {
      return;
    }
    for (const pageItem of portalPages) {
      if (pageItem && pageItem.noteIds && -1 !== pageItem.noteIds.indexOf(noteId)) {
        for (const rootType of [MenuType.PORTAL_SIDEBAR, MenuType.PORTAL_TOPBAR, MenuType.PORTAL_FOOTER]) {
          for (const menuItemId in menuState[rootType].entities) {
            const item = menuState[rootType].entities[menuItemId];
            if ('pageId' in item && item.pageId === pageItem.id) {
              setNoteInMenu(item);
              return;
            }
          }
        }
      }
    }

    setNoteInMenu(null);
  }, 100);

  useEffect(() => {
    let found = false;
    for (const rootType of [MenuType.PORTAL_SIDEBAR, MenuType.PORTAL_TOPBAR, MenuType.PORTAL_FOOTER]) {
      for (const menuItemId in menuState[rootType].entities) {
        const item = menuState[rootType].entities[menuItemId];
        if ('targetId' in item && item.targetId === note?.globalId) {
          setNoteInMenu(item);
          found = true;
          break;
        }
      }
      if (found) {
        // to avoid race conditions cancelling debounced function and tell the function
        // that it should cancel execution if it already started (because the function is async)
        // Race condition have negative impact here because debounced function might have
        // old menuState object and then set noteInMenu to null
        isFetchPortalPageExecutionCanceled.current = true;
        fetchPortalPagesDebounced.cancel();
        break;
      }
    }

    // if current note is fetching we might have not actual note data (from the previous visited note)
    // so we don't want to fetch note menu item using portal pages now
    if (!found && !isNoteFetching) {
      fetchPortalPagesDebounced({
        workspaceId: note?.workspaceId,
        noteId: note?.globalId,
        menuState,
      });
    } else {
      fetchPortalPagesDebounced.cancel();
    }
  }, [note, menuState, workspaceId, fetchPortalPagesDebounced, isNoteFetching]);

  return noteInMenu;
};
